import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input, Button } from '../../components/ui';
import { Lock, Mail } from 'lucide-react';
import { signIn, AuthError } from '../../lib/auth';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!email) newErrors.email = 'loginPage.validation.emailRequired';
    if (!password) newErrors.password = 'loginPage.validation.passwordRequired';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setErrors({});
    setIsLoading(true);
    try {
      await signIn(email, password);
      navigate('/');
    } catch (error: any) {
      const authError = error as AuthError;
      addToast({
        type: 'error',
        message: authError.message || t('auth.errors.unknown'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleSubmit();
    }
  };

  const getErrorMessage = (field: string): string | undefined => {
    const errorKey = errors[field];
    return errorKey ? t(errorKey) : undefined;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <img
            src="/praxi-logo.png"
            alt="Praxi"
            className="h-12 mx-auto mb-6"
          />
          <h1 className="text-3xl font-bold text-gray-900">
            {t('loginPage.title')}
          </h1>
          <p className="mt-2 text-gray-600">
            {t('loginPage.subtitle')}
          </p>
        </div>

        <div className="bg-white p-8 rounded-xl shadow-md border border-gray-200">
          <div className="space-y-6">
            <Input
              label={t('loginPage.form.emailLabel')}
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setErrors((prev) => ({ ...prev, email: undefined }));
              }}
              error={getErrorMessage('email')}
              leftIcon={<Mail className="w-5 h-5 text-gray-400" />}
              placeholder={t('loginPage.form.emailPlaceholder')}
              required
              disabled={isLoading}
              onKeyPress={handleKeyPress}
              autoComplete="email"
            />

            <Input
              label={t('loginPage.form.passwordLabel')}
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setErrors((prev) => ({ ...prev, password: undefined }));
              }}
              error={getErrorMessage('password')}
              leftIcon={<Lock className="w-5 h-5 text-gray-400" />}
              placeholder={t('loginPage.form.passwordPlaceholder')}
              required
              disabled={isLoading}
              onKeyPress={handleKeyPress}
              autoComplete="current-password"
            />

            <div className="flex items-center justify-between">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  disabled={isLoading}
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span className="ml-2 text-sm text-gray-700">
                  {t('loginPage.form.rememberMe')}
                </span>
              </label>

              <Link
                to="/forgot-password"
                className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
              >
                {t('loginPage.form.forgotPasswordLink')}
              </Link>
            </div>

            <Button
              variant="primary"
              className="w-full"
              onClick={handleSubmit}
              isLoading={isLoading}
              disabled={isLoading}
            >
              {t('loginPage.form.submitButton')}
            </Button>
          </div>
          
          <p className="mt-8 text-center text-sm text-gray-600">
            {t('loginPage.signUpPrompt')}{' '}
            <Link
              to="/register"
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              {t('loginPage.signUpLink')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;